import { useEffect, useState } from 'react'
import Renderer, { Renderable } from '../classes/renderer'

const webGLContextErrorMessage = 'Unable to get WebGL context.'

type RendererOptions = {
    desynchronized?: boolean
    preserveDrawingBuffer?: boolean
}

export function useRenderer(
    canvas: HTMLCanvasElement | null,
    nodes?: Renderable[],
    transparency?: boolean,
    onError?: (error: Error) => void,
    options: RendererOptions = {}
) {
    const [state, setState] = useState<Renderer | null>(null)
    const { desynchronized = true, preserveDrawingBuffer = false } = options

    useEffect(() => {
        if (!canvas) return

        const gl = canvas.getContext('webgl', {
            alpha: transparency || false,
            antialias: false,
            depth: false,
            desynchronized,
            powerPreference: 'high-performance',
            premultipliedAlpha: true,
            preserveDrawingBuffer,
        })

        if (!gl) {
            const error = new Error(webGLContextErrorMessage)

            console.error(error.message, error)
            setState(null)
            onError?.(error)
            return
        }

        setState(new Renderer(gl))
    }, [canvas, desynchronized, onError, preserveDrawingBuffer, transparency])

    useEffect(() => {
        if (!state || !nodes) return

        state.setNodes(nodes)
    }, [nodes, state])

    return state
}
